/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useMemo, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { Button, Table, type TableColumnsType, type TableProps } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { DndContext, useSensors, useSensor, PointerSensor } from '@dnd-kit/core';
import { type SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const TableWrapper = styled.div`
  .ant-table-container {
    .ant-table-content {
      .ant-table-cell {
        padding: 8px 12px;
      }
    }
  }
`;

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});
const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

interface DraggableTableProps<TDataType> extends Omit<TableProps<TDataType>, 'columns' | 'dataSource' | 'components'> {
  rowKey: string;
  columns?: TableColumnsType<TDataType>;
  dataSource?: TDataType[];
  onChange?: (value: any) => void;
}

const DraggableRow = ({ children, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, setActivatorNodeRef } = useSortable({
    id: props['data-row-key']
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {})
  };
  const contextValue = useMemo<RowContextProps>(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes}>
        {children}
      </tr>
    </RowContext.Provider>
  );
};

const DraggableTable = <TDataType extends object>({
  rowKey,
  columns,
  dataSource,
  onChange,
  ...props
}: DraggableTableProps<TDataType>) => {
  const dragHandleColumn = { [rowKey]: 'sort', align: 'center', width: '30px', render: () => <DragHandle /> };
  const [sortColumns] = useState([dragHandleColumn, ...columns]);
  const [srotDataSource, setDataSource] = useState(dataSource);

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    setDataSource(dataSource);
  }, [dataSource]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (rowKey && active && over && active?.id !== over?.id) {
      setDataSource((prev) => {
        const oldIndex = prev.findIndex((item) => item[rowKey] === active.id);
        const newIndex = prev.findIndex((item) => item[rowKey] === over.id);
        const array = arrayMove(prev, oldIndex, newIndex);

        onChange(array);
        return array;
      });
    }
  };

  return (
    <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
      <SortableContext items={srotDataSource?.map((item) => item[rowKey]) || []} strategy={verticalListSortingStrategy}>
        <TableWrapper>
          <Table
            {...props}
            key={dataSource?.length}
            components={{ body: { row: DraggableRow } }}
            rowKey={rowKey}
            dataSource={srotDataSource}
            columns={sortColumns as TableColumnsType<TDataType>}
          />
        </TableWrapper>
      </SortableContext>
    </DndContext>
  );
};

export default DraggableTable;
